import { faPencil, faPlus, faSave } from '@fortawesome/pro-regular-svg-icons'
import { Box, Button, Card, Flex, Skeleton, Text } from '@mantine/core'
import { useLayoutEffect, useRef, useState } from 'react'

import { useMutationUpdateInsurance } from '@flecto/core/saas'
import { useTranslation } from '@flecto/translations'
import { type UNSAFE_any, type CoreInsurance } from '@flecto/types'
import { Icon, RichTextEditor, useNotifications } from '@flecto/ui'

export function InsuranceNotes({ insurance }: { insurance: CoreInsurance }) {
  const { t } = useTranslation('saas', { keyPrefix: 'insurances' })
  const { t: tActions } = useTranslation('shared', { keyPrefix: 'actions' })
  const { showNotification } = useNotifications()
  const [note, setNote] = useState('')
  const [isEditing, setIsEditing] = useState(false)
  const [addingNote, setAddingNote] = useState(false)

  const editorRef = useRef<UNSAFE_any>(null)

  const { mutateAsync: updateInsurance, isLoading } =
    useMutationUpdateInsurance()

  const handleAddNote = async () => {
    try {
      await updateInsurance({
        insurance: {
          internal_id: insurance?.internal_id,
          notes: note,
        },
      })
      setAddingNote(false)
      setIsEditing(false)

      showNotification({
        type: 'success',
        message: t('insurances_details.notes_notification_success'),
      })
    } catch {
      showNotification({
        type: 'error',
        message: t('insurances_details.notes_notification_error'),
      })
    }
  }

  useLayoutEffect(() => {
    if (isEditing || addingNote) {
      if (editorRef.current) {
        requestAnimationFrame(() => {
          editorRef.current?.focus?.()

          const editableElement = editorRef.current?.querySelector(
            '[contenteditable="true"]'
          )
          if (editableElement) {
            ;(editableElement as HTMLElement).focus()

            if (addingNote) {
              const element = document.getElementById('btn-save')

              element?.scrollIntoView({ behavior: 'smooth', block: 'end' })
            }
          }
        })
      }
    }
  }, [isEditing, addingNote])

  return (
    <Card p='lg'>
      <Flex align='center' justify='space-between' mb='lg'>
        <Box style={{ flex: 1 }}>
          <Text fw={500}>{t('insurances_details.notes_title')}</Text>
          <Text c='dimmed' size='sm'>
            {t('insurances_details.notes_description')}
          </Text>
        </Box>
      </Flex>
      {isLoading ? (
        <Skeleton height={100} />
      ) : insurance?.notes && insurance?.notes !== '<p></p>' && !addingNote ? (
        <Flex gap='md' direction='column'>
          <Box ref={editorRef}>
            <RichTextEditor
              value={insurance?.notes}
              readOnly={!isEditing}
              withToolbar={false}
              onChange={(value) => setNote(String(value))}
              key={isEditing ? 'edit' : 'view'}
            />
          </Box>

          {isEditing ? (
            <Flex gap='md'>
              <Button
                variant='filled'
                leftSection={<Icon icon={faSave} size='sm' />}
                color='green'
                size='sm'
                onClick={handleAddNote}
                id='btn-save'
              >
                <Text size='md' fw={500}>
                  {tActions('save')}
                </Text>
              </Button>
              <Button
                variant='default'
                color='gray'
                size='sm'
                onClick={() => {
                  setNote(insurance?.notes ?? '')
                  setIsEditing(false)
                }}
              >
                <Text size='md' fw={500}>
                  {tActions('cancel')}
                </Text>
              </Button>
            </Flex>
          ) : (
            <Button
              variant='outline'
              leftSection={<Icon icon={faPencil} size='sm' />}
              color='gray'
              size='sm'
              onClick={() => {
                setIsEditing(true)
              }}
              w={'fit-content'}
            >
              <Text size='md' fw={500}>
                {t('insurances_details.notes_edit_btn')}
              </Text>
            </Button>
          )}
        </Flex>
      ) : addingNote ? (
        <Flex gap='md' direction='column'>
          <Box ref={editorRef}>
            <RichTextEditor
              value={note}
              withToolbar={false}
              onChange={(value) => setNote(String(value))}
            />
          </Box>
          <Flex gap='md'>
            <Button
              variant='filled'
              leftSection={<Icon icon={faSave} size='sm' />}
              color='green'
              size='sm'
              onClick={handleAddNote}
              id='btn-save'
            >
              <Text size='md' fw={500}>
                {tActions('save')}
              </Text>
            </Button>
            <Button
              variant='default'
              color='gray'
              size='sm'
              onClick={() => {
                setNote('')
                setAddingNote(false)
              }}
            >
              <Text size='md' fw={500}>
                {tActions('cancel')}
              </Text>
            </Button>
          </Flex>
        </Flex>
      ) : (
        <Button
          variant='outline'
          leftSection={<Icon icon={faPlus} size='sm' />}
          color='green'
          size='sm'
          onClick={() => setAddingNote(true)}
        >
          <Text size='md' fw={500}>
            {t('insurances_details.notes_add_btn')}
          </Text>
        </Button>
      )}
    </Card>
  )
}
